import { useCallback, useEffect, useState } from 'react';
import type { InitialState, ModalState } from '../types';

interface UseDeliveryModalStageParams {
  initialState: InitialState | null;
  defState: boolean;
  prepareNewAddress: () => void;
}

interface UseDeliveryModalStageReturn {
  modalStage: ModalState;
  setModalStage: (s: ModalState) => void;
  isShowComment: boolean;
  toggleShowComment: () => void;
  startNewAddress: () => void;
}

/**
 * Owns only the modal's navigation/UI state (which stage is shown, comment
 * visibility). The editable address fields (fio/phone/flat/comment) live in the
 * parent's `useAddressForm` — this hook no longer duplicates them.
 */
const useDeliveryModalStage = ({
  initialState,
  defState,
  prepareNewAddress
}: UseDeliveryModalStageParams): UseDeliveryModalStageReturn => {
  const [modalStage, setModalStage] = useState<ModalState>('list');
  const [isShowComment, setIsShowComment] = useState(false);

  const toggleShowComment = useCallback(() => {
    setIsShowComment(v => !v);
  }, []);

  const startNewAddress = useCallback(() => {
    // Parent seeds the address fields (incl. user prefill) via prepareNewAddress.
    prepareNewAddress();
    setModalStage('addAddress');
  }, [prepareNewAddress]);

  useEffect(() => {
    // The parent loads `initialState` into its address fields; here we only
    // jump the modal to the edit stage.
    if (initialState) {
      setModalStage('editAddress');
    }
  }, [initialState]);

  // Срабатывает только на само открытие с defState: identity startNewAddress
  // может меняться на ре-рендерах, повторный вызов сбрасывал бы форму/стадию.
  useEffect(() => {
    if (defState) {
      startNewAddress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defState]);

  return {
    modalStage,
    setModalStage,
    isShowComment,
    toggleShowComment,
    startNewAddress
  };
};

export default useDeliveryModalStage;
